import React, { useState, useEffect, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";

import HubTabs from "../../components/HubTabs";
import SchedulesForm from "../../components/SchedulesForm";
import PlansManager from "../../components/PlansManager";
import HelpsManager from "../../components/HelpsManager";
import Options from "../../components/Settings/Options";
import Whitelabel from "../../components/Settings/Whitelabel";
import TimezoneTab from "../../components/Settings/TimezoneTab";
import SMTPConfig from "../../components/Settings/SMTPConfig";
import MetaIntegration from "../../components/Settings/MetaIntegration";

import { i18n } from "../../translate/i18n.js";

import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import useSettings from "../../hooks/useSettings";
import ForbiddenPage from "../../components/ForbiddenPage/index.js";

const useStyles = makeStyles((theme) => ({
  schedulesTabContainer: {
    flex: 1,
    minHeight: 0,
    height: "100%",
    padding: 12,
    overflowY: "auto",
    overflowX: "hidden",
    ...(theme.scrollbarStyles || {}),
  },
}));

const SettingsCustom = () => {
  const classes = useStyles();
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { find, updateSchedules } = useCompanies();
  const { getAll: getAllSettings } = useCompanySettings();
  const { getAll: getAllSettingsOld } = useSettings();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    async function findData() {
      setLoading(true);
      try {
        const companyId = user.companyId;
        const company = await find(companyId);
        const settingList = await getAllSettings(companyId);
        const settingListOld = await getAllSettingsOld();

        setCompany(company);
        setSchedules(company.schedules);
        setSettings(settingList);
        setOldSettings(settingListOld);
        setSchedulesEnabled(settingList.scheduleType === "company");
        setCurrentUser(user);
      } catch (e) {
        toast.error(e);
      }
      setLoading(false);
    }
    findData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitSchedules = async (data, extras = {}) => {
    setLoading(true);
    try {
      setSchedules(data);
      const payload = { id: company.id, schedules: data };
      if (typeof extras.outOfHoursMessage === "string") {
        payload.outOfHoursMessage = extras.outOfHoursMessage;
        setCompany((prev) => ({ ...prev, outOfHoursMessage: extras.outOfHoursMessage }));
      }
      if (typeof extras.keepFlowsOutOfHours === "boolean") {
        payload.keepFlowsOutOfHours = extras.keepFlowsOutOfHours;
        setCompany((prev) => ({ ...prev, keepFlowsOutOfHours: extras.keepFlowsOutOfHours }));
      }
      await updateSchedules(payload);
      toast.success(i18n.t("settings.toasts.schedulesSavedSuccess"));
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  const isSuper = currentUser.super;

  const tabs = useMemo(() => {
    const list = [
      {
        label: i18n.t("settings.tabs.options"),
        path: "/settings",
        Component: () => (
          <Options
            settings={settings}
            oldSettings={oldSettings}
            user={currentUser}
            scheduleTypeChanged={(value) =>
              setSchedulesEnabled(value === "company")
            }
          />
        ),
      },
      schedulesEnabled && {
        label: i18n.t("settings.tabs.schedules"),
        path: "/settings/schedules",
        Component: () => (
          <div className={classes.schedulesTabContainer}>
            <SchedulesForm
              loading={loading}
              onSubmit={handleSubmitSchedules}
              initialValues={schedules}
              showOutOfHoursMessage
              initialOutOfHoursMessage={company?.outOfHoursMessage || ""}
              initialKeepFlowsOutOfHours={!!company?.keepFlowsOutOfHours}
            />
          </div>
        ),
      },
      {
        label: i18n.t("settings.tabs.timezone"),
        path: "/settings/timezone",
        Component: TimezoneTab,
      },
      {
        label: i18n.t("settings.tabs.smtp"),
        path: "/settings/smtp",
        Component: () => <SMTPConfig settings={settings} />,
      },
      {
        label: i18n.t("settings.tabs.meta"),
        path: "/settings/meta",
        Component: MetaIntegration,
      },
      isSuper && {
        label: i18n.t("settings.tabs.plans"),
        path: "/settings/plans",
        Component: PlansManager,
      },
      isSuper && {
        label: i18n.t("settings.tabs.helps"),
        path: "/settings/helps",
        Component: HelpsManager,
      },
      isSuper && {
        label: i18n.t("settings.tabs.whitelabel"),
        path: "/settings/whitelabel",
        Component: () => <Whitelabel settings={oldSettings} />,
      },
    ];
    return list.filter(Boolean);
  }, [
    settings,
    oldSettings,
    currentUser,
    schedules,
    schedulesEnabled,
    loading,
    isSuper,
  ]);

  if (user.profile === "user") return <ForbiddenPage />;

  return <HubTabs tabs={tabs} />;
};

export default SettingsCustom;
